import { StorageInterface } from '../stores/childNavStores';
import { 
  AppProfileDto, 
  AppProfileQuery, 
  AppProfilePHIDto,
  MVarDataDto,
  createEmptyAppProfileDto,
  updateAppProfileModified,
  updateMVarData,
  removeMVarData,
  getMVarDataValue,
  createMVarData
} from '../types/api/appProfile';

/**
 * AppProfileStorage Service
 * Manages AppProfileDto storage and operations for backend synchronization
 */
export class AppProfileStorage {
  private storage: StorageInterface;
  private storageKey: string = 'child-nav-app-profiles';

  constructor(storage: StorageInterface) {
    this.storage = storage;
  }

  // Core CRUD Operations
  
  /**
   * Get all app profiles
   */
  async getAllProfiles(): Promise<AppProfileDto[]> {
    try {
      const data = await this.storage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('AppProfileStorage: Error loading profiles:', error);
      return [];
    }
  }

  /**
   * Get app profile by ID
   */
  async getProfile(profileId: string): Promise<AppProfileDto | null> {
    try {
      const profiles = await this.getAllProfiles();
      const profile = profiles.find(p => p.profileId === profileId) || null;
      return profile;
    } catch (error) {      console.error('AppProfileStorage: Error getting profile:', error);
      return null;
    }
  }

  /**
   * Save or update app profile
   */
  async saveProfile(profile: AppProfileDto): Promise<void> {
    try {
      const profiles = await this.getAllProfiles();
      const existingIndex = profiles.findIndex(p => p.profileId === profile.profileId);
      
      const updatedProfile = updateAppProfileModified(profile);
      
      if (existingIndex >= 0) {
        profiles[existingIndex] = updatedProfile;
      } else {
        profiles.push(updatedProfile);
      }

      console.log('🔎 AppProfileStorage.saveProfile track counts:', {
        profileId: profile.profileId,
        prescriptions: updatedProfile.prescriptions?.events?.length ?? 0,
        referrals: updatedProfile.referrals?.events?.length ?? 0,
      });
      
      await this.storage.setItem(this.storageKey, JSON.stringify(profiles));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete app profile
   */
  async deleteProfile(profileId: string): Promise<void> {
    try {
      const profiles = await this.getAllProfiles();
      const filteredProfiles = profiles.filter(p => p.profileId !== profileId);
      
      await this.storage.setItem(this.storageKey, JSON.stringify(filteredProfiles));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create new empty profile
   */
  async createProfile(profileId: string, accountId: string): Promise<AppProfileDto> {
    try {
      const profile = createEmptyAppProfileDto(profileId, accountId);
      await this.saveProfile(profile);
      return profile;
    } catch (error) {
      throw error;
    }
  }

  // PHI Management

  /**
   * Update profile PHI information
   */
  async updatePHI(profileId: string, phi: Partial<AppProfilePHIDto>): Promise<void> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      profile.phi = { ...profile.phi, ...phi };
      await this.saveProfile(profile);
    } catch (error) {
      throw error;
    }
  }

  // MVar Data Management

  /**
   * Update milestone data
   */
  async updateMilestone(profileId: string, name: string, value: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      profile.milestones = updateMVarData(profile.milestones, name, value);
      await this.saveProfile(profile);
     } catch (error) {
      throw error;
    }
  }

  /**
   * Update concern data
   */
  async updateConcern(profileId: string, name: string, value: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      profile.concerns = updateMVarData(profile.concerns, name, value);
      await this.saveProfile(profile);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update goal data
   */
  async updateGoal(profileId: string, name: string, value: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      profile.goals = updateMVarData(profile.goals, name, value);
      await this.saveProfile(profile); 
    } catch (error) {
     throw error;
    }
  }

  /**
   * Update action data
   */
  async updateAction(profileId: string, name: string, value: string): Promise<void> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) {
        throw new Error(`Profile not found: ${profileId}`);
      }

      profile.actions = updateMVarData(profile.actions as any, name, value) as any;
      await this.saveProfile(profile);
    } catch (error) {
      throw error;
    }
  }

  // Admin State Management - Currently Deprecated
  // NOTE: AdminStateDto objects should be managed via AdminStateStorage
  // The adminstates field in AppProfileDto is loaded by matching profileId
  
  /**
   * @deprecated Use AdminStateStorage to manage AdminStateDto objects directly
   * Add or update admin record
   */
  async updateAdmin(profileId: string, recId: string, data: MVarDataDto[]): Promise<void> {
    console.warn('AppProfileStorage.updateAdmin is deprecated. Use AdminStateStorage for AdminStateDto management.');
    throw new Error('updateAdmin is deprecated - use AdminStateStorage to manage AdminStateDto objects');
  }

  /**
   * @deprecated Use AdminStateStorage to manage AdminStateDto objects directly
   * Remove admin record
   */
  async removeAdmin(profileId: string, recId: string): Promise<void> {
    console.warn('AppProfileStorage.removeAdmin is deprecated. Use AdminStateStorage for AdminStateDto management.');
    throw new Error('removeAdmin is deprecated - use AdminStateStorage to manage AdminStateDto objects');
  }

  // Utility Methods

  /**
   * Get milestone value
   */
  async getMilestoneValue(profileId: string, name: string): Promise<string | null> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) return null;
      
      return getMVarDataValue(profile.milestones, name);
    } catch (error) {
      console.error('AppProfileStorage: Error getting milestone value:', error);
      return null;
    }
  }

  /**
   * Get concern value
   */
  async getConcernValue(profileId: string, name: string): Promise<string | null> {
    try {
      const profile = await this.getProfile(profileId);
      if (!profile) return null;
      
      return getMVarDataValue(profile.concerns, name);
    } catch (error) {
      console.error('AppProfileStorage: Error getting concern value:', error);
      return null;
    }
  }

  /**
   * Check if profile exists
   */
  async profileExists(profileId: string): Promise<boolean> {
    try {
      const profile = await this.getProfile(profileId);
      return profile !== null;
    } catch (error) {
      console.error('AppProfileStorage: Error checking profile existence:', error);
      return false;
    }
  }

  /**
   * Get profiles by account ID
   */
  async getProfilesByAccount(accountId: string): Promise<AppProfileDto[]> {
    try {
      const profiles = await this.getAllProfiles();
      return profiles.filter(p => p.accountId === accountId);
    } catch (error) {
      console.error('AppProfileStorage: Error getting profiles by account:', error);
      return [];
    }
  }

  /**
   * Clear all profiles (for testing/reset)
   */
  async clearAllProfiles(): Promise<void> {
    try {
      await this.storage.removeItem(this.storageKey);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    totalProfiles: number;
    profilesByAccount: Record<string, number>;
    totalMilestones: number;
    totalConcerns: number;
    totalGoals: number;
    totalActions: number;
    totalAdminRecords: number;
  }> {
    try {
      const profiles = await this.getAllProfiles();
      const stats = {
        totalProfiles: profiles.length,
        profilesByAccount: {} as Record<string, number>,
        totalMilestones: 0,
        totalConcerns: 0,
        totalGoals: 0,
        totalActions: 0,
        totalAdminRecords: 0,
      };

      profiles.forEach(profile => {
        // Count by account
        stats.profilesByAccount[profile.accountId] = 
          (stats.profilesByAccount[profile.accountId] || 0) + 1;

        // Count data items
        stats.totalMilestones += profile.milestones.length;
        stats.totalConcerns += profile.concerns.length;
        stats.totalGoals += profile.goals.length;
        stats.totalActions += profile.actions.length;
        stats.totalAdminRecords += profile.adminstates.length;
      });

      return stats;
    } catch (error) {
      console.error('AppProfileStorage: Error getting stats:', error);
      return {
        totalProfiles: 0,
        profilesByAccount: {},
        totalMilestones: 0,
        totalConcerns: 0,
        totalGoals: 0,
        totalActions: 0,
        totalAdminRecords: 0,
      };
    }
  }
}